import React from 'react'
import { cn } from '@/lib/utils'
import { useSelector, useDispatch } from 'react-redux'
import { RootState } from '@/store'
import { updatePreviewResponse } from '@/store/slices/preview'
import EmptyStarIcon from '@/assets/builder/icon/EmptyStars'
import FilledStarIcon from '@/assets/builder/icon/FilledStar'

interface RatingScaleProps {
    onAutoProceed?: () => void
    isTransitioning?: boolean
}

const RatingScale: React.FC<RatingScaleProps> = ({
    onAutoProceed,
    isTransitioning = false,
}) => {
    const dispatch = useDispatch()
    const { selectedQuestionId } = useSelector((state: any) => state.preview)
    const question = useSelector((state: RootState) =>
        state.preview.questions.find(
            (q: any) => q.questionId === selectedQuestionId
        )
    )
    const savedResponse = useSelector(
        (state: RootState) => state.preview.previewResponses[selectedQuestionId]
    )
    if (!question) return null
    const displayType = question.config.displayType
    const minRating = 1
    const maxRating = 5
    const divisions = maxRating - minRating + 1
    const handleRatingClick = (rating: number) => {
        // Prevent selection during transition
        if (isTransitioning) return

        dispatch(
            updatePreviewResponse({
                questionId: selectedQuestionId,
                response: { value: rating },
            })
        )

        // Auto-proceed after brief delay for visual feedback
        if (onAutoProceed) {
            setTimeout(() => {
                onAutoProceed()
            }, 300)
        }
    }
    if (displayType === 'star') {
        return (
            <div>
                <div className="flex justify-between w-full gap-2 mt-4">
                    {Array.from({ length: divisions }).map((_, index) => (
                        <button
                            key={index}
                            onClick={() => handleRatingClick(index + minRating)}
                            className="cursor-pointer"
                        >
                            {savedResponse &&
                            index + minRating <=
                                Number(savedResponse?.value) ? (
                                <FilledStarIcon fill="#FFD82C" />
                            ) : (
                                <EmptyStarIcon />
                            )}
                        </button>
                    ))}
                </div>
                <div className="flex w-full mt-3 px-2 text-sm text-[#222] font-medium">
                    <span className="flex-1 text-left">
                        {question?.config?.minLabel || 'Terrible'}
                    </span>
                    <span className="flex-1 text-right">
                        {question?.config?.maxLabel || 'Excellent'}
                    </span>
                </div>
            </div>
        )
    }
    if (displayType === 'emoji') {
        // Emoji scale for rating
        const emojiLabels = [
            { emoji: '😡', label: 'Terrible' },
            { emoji: '🙁', label: 'Bad' },
            { emoji: '🙂', label: 'Good' },
            { emoji: '😁', label: 'Very good' },
            { emoji: '🤩', label: 'Excellent' },
        ]
        return (
            <div className="flex justify-between w-full mt-4">
                {emojiLabels.map((item, index) => {
                    const value = index + 1
                    const isActive =
                        savedResponse && value === Number(savedResponse?.value)
                    return (
                        <button
                            key={index}
                            onClick={() => handleRatingClick(value)}
                            className={cn(
                                'flex flex-col items-center justify-center px-4 py-2 rounded-lg transition',
                                isActive ? 'bg-gray-200' : 'bg-transparent',
                                'focus:outline-none border-none'
                            )}
                            style={{ minWidth: 35 }}
                        >
                            <span className="mb-1 text-2xl scale-150">
                                {item.emoji}
                            </span>
                            <span className="text-[14px] whitespace-nowrap font-medium text-[#222]">
                                {item.label}
                            </span>
                        </button>
                    )
                })}
            </div>
        )
    }

    return (
        <div className="w-full max-w-[470px] mt-4">
            <div className="flex w-full">
                {Array.from({ length: divisions }).map((_, index) => (
                    <button
                        key={index}
                        onClick={() => handleRatingClick(index + minRating)}
                        className={cn(
                            'flex-1 h-[56px] flex items-center justify-center text-md font-semibold transition-all border border-[#DEDEDE] bg-white hover:bg-gray-50',
                            Number(savedResponse?.value) ===
                                index + minRating &&
                                'bg-[#F3F4F6] border border-gray-400 text-[#222] font-bold',
                            index === 0 && 'rounded-l-lg',
                            index === divisions - 1 && 'rounded-r-lg',
                            'focus:outline-none'
                        )}
                        style={{ minWidth: 0 }}
                    >
                        {index + minRating}
                    </button>
                ))}
            </div>
            <div className="flex w-full mt-3 px-2 text-sm text-[#222] font-medium">
                <span className="flex-1 text-left">
                    {question?.config?.minLabel || 'Terrible'}
                </span>
                <span className="flex-1 text-right">
                    {question?.config?.maxLabel || 'Excellent'}
                </span>
            </div>
        </div>
    )
}

export default RatingScale
